import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Alert, Timeline, Badge, Divider } from 'antd';
import { 
  BugOutlined, 
  ReloadOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  DisconnectOutlined
} from '@ant-design/icons';
import simpleErrorHandler from '../utils/simpleErrorHandler';

const { Title, Text, Paragraph } = Typography;

interface SocketEvent {
  timestamp: Date;
  type: 'connect' | 'disconnect' | 'error' | 'reconnect' | 'reconnect_attempt' | 'reconnect_error' | 'reconnect_failed';
  message: string;
  details?: any;
}

const SocketDiagnostic: React.FC = () => {
  const [socket, setSocket] = useState<any>(null);
  const [events, setEvents] = useState<SocketEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [errorCount, setErrorCount] = useState(0);

  const addEvent = (type: SocketEvent['type'], message: string, details?: any) => {
    const event: SocketEvent = {
      timestamp: new Date(),
      type,
      message,
      details
    };
    setEvents(prev => [event, ...prev].slice(0, 50)); // 保留最近50个事件
  };

  useEffect(() => {
    const initSocket = () => {
      if ((window as any).io) {
        const socketInstance = (window as any).io;
        setSocket(socketInstance);

        // 监听所有Socket.IO事件
        socketInstance.on('connect', () => {
          console.log('🔌 Socket.IO 连接成功');
          setIsConnected(true);
          setConnectionState('connected');
          addEvent('connect', '连接成功');
        });

        socketInstance.on('disconnect', (reason: string) => {
          console.log('🔌 Socket.IO 断开连接:', reason);
          setIsConnected(false);
          setConnectionState('disconnected');
          addEvent('disconnect', `断开连接: ${reason}`);
        });

        socketInstance.on('connect_error', (error: any) => {
          console.error('🔌 Socket.IO 连接错误:', error);
          setIsConnected(false);
          setConnectionState('error');
          setErrorCount(prev => prev + 1);
          addEvent('error', `连接错误: ${error.message || '未知错误'}`, error);
          
          // 记录到错误日志
          simpleErrorHandler.logError(error, 'SocketDiagnostic');
        });

        socketInstance.on('reconnect', (attemptNumber: number) => {
          console.log('🔌 Socket.IO 重连成功，尝试次数:', attemptNumber);
          setIsConnected(true);
          setConnectionState('reconnected');
          addEvent('reconnect', `重连成功，尝试次数: ${attemptNumber}`);
        });

        socketInstance.on('reconnect_attempt', (attemptNumber: number) => {
          console.log('🔌 Socket.IO 重连尝试:', attemptNumber);
          setConnectionState('reconnecting');
          addEvent('reconnect_attempt', `重连尝试: ${attemptNumber}`);
        });

        socketInstance.on('reconnect_error', (error: any) => {
          console.error('🔌 Socket.IO 重连错误:', error);
          setConnectionState('reconnect_error');
          addEvent('reconnect_error', `重连错误: ${error.message || '未知错误'}`, error);
        });

        socketInstance.on('reconnect_failed', () => {
          console.error('🔌 Socket.IO 重连失败');
          setConnectionState('reconnect_failed');
          addEvent('reconnect_failed', '重连失败');
        });

        // 检查初始连接状态
        setIsConnected(socketInstance.connected);
        setConnectionState(socketInstance.connected ? 'connected' : 'disconnected');
        addEvent('connect', 'Socket.IO 初始化完成');
      } else {
        addEvent('error', 'Socket.IO 未加载');
      }
    };

    // 延迟初始化
    const timer = setTimeout(initSocket, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  const handleReconnect = () => {
    if (socket) {
      console.log('🔌 手动重连 Socket.IO');
      addEvent('reconnect_attempt', '手动重连');
      socket.disconnect();
      socket.connect();
    }
  };

  const handleClearEvents = () => {
    setEvents([]);
  };

  const getEventIcon = (type: SocketEvent['type']) => {
    switch (type) {
      case 'connect':
      case 'reconnect':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'disconnect':
        return <DisconnectOutlined style={{ color: '#ff4d4f' }} />;
      case 'error':
      case 'reconnect_error':
      case 'reconnect_failed':
        return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'reconnect_attempt':
        return <ClockCircleOutlined style={{ color: '#faad14' }} />;
      default:
        return <BugOutlined />;
    }
  };

  const getEventColor = (type: SocketEvent['type']) => {
    switch (type) {
      case 'connect':
      case 'reconnect':
        return 'green';
      case 'disconnect':
        return 'red';
      case 'error':
      case 'reconnect_error':
      case 'reconnect_failed':
        return 'red';
      case 'reconnect_attempt':
        return 'orange';
      default:
        return 'blue';
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>
              <BugOutlined /> Socket.IO 连接诊断
            </Title>
            <Text type="secondary">实时监控Socket.IO连接状态和事件</Text>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleReconnect} disabled={!socket}>
              手动重连
            </Button>
            <Button onClick={handleClearEvents}>
              清空事件
            </Button>
          </Space>
        </div>

        {/* 连接状态 */}
        <Alert
          message={`连接状态: ${isConnected ? '已连接' : '未连接'}`}
          description={`状态: ${connectionState} | 错误次数: ${errorCount}`}
          type={isConnected ? 'success' : 'error'}
          style={{ marginBottom: '20px' }}
        />

        {/* 事件时间线 */}
        <Card title="连接事件时间线" size="small">
          {events.length === 0 ? (
            <Text type="secondary">暂无事件记录</Text>
          ) : (
            <Timeline>
              {events.map((event, index) => (
                <Timeline.Item
                  key={index}
                  dot={getEventIcon(event.type)}
                  color={getEventColor(event.type)}
                >
                  <div>
                    <Text strong>{event.message}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {event.timestamp.toLocaleString()}
                    </Text>
                    {event.details && (
                      <div style={{ marginTop: '4px' }}>
                        <Text code style={{ fontSize: '11px' }}>
                          {JSON.stringify(event.details, null, 2)}
                        </Text>
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          )}
        </Card>

        <Divider />

        {/* 诊断信息 */}
        <Card title="诊断信息" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Socket.IO 版本: </Text>
              <Text code>{(window as any).io?.version || '未知'}</Text>
            </div>
            <div>
              <Text strong>传输方式: </Text>
              <Text code>{socket?.io?.engine?.transport?.name || '未知'}</Text>
            </div>
            <div>
              <Text strong>连接ID: </Text>
              <Text code>{socket?.id || '未连接'}</Text>
            </div>
            <div>
              <Text strong>错误日志: </Text>
              <Text code>{simpleErrorHandler.getErrors().length} 条记录</Text>
            </div>
          </Space>
        </Card>
      </Card>
    </div>
  );
};

export default SocketDiagnostic;
